// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";

import type { State } from "@store/reducers";
import {
  Loader,
  View,
  ManagerSmsNotificationsSmsList,
  ManagerSmsNotificationEditModal,
  ManagerSmsNotificationDeleteModal,
} from "@components";
import withErrorHandler from "@components/error";

import serviceActionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { HierarchyAssignment } from "@modules/service/types";
import type {
  ManagerSmsRestriction,
  ManagerSmsSettings,
} from "@modules/service/reducer/manager-sms-notifications/types";
import type { EmployeeSmsSettings } from "@modules/service/reducer/employee-sms-notifications/types";
import { getUserId } from "@modules/account/selectors";

type PropsFromState = $Exact<{
  isLoading?: boolean,
  // SMS Absence Notifications
  currentUserId: number | null,
  managerSmsSettings: ?ManagerSmsSettings,
  getManagerSmsNotificationsList: Array<ManagerSmsRestriction>,
  getManagerSmsCurrent: ?ManagerSmsRestriction,
  getManagerSmsAssignments: Array<HierarchyAssignment>,
  getShowAddManagerSmsNotificationSmsRuleModal: boolean,
  getShowEditManagerSmsNotificationModal: boolean,
  getShowDeleteManagerSmsNotificationSmsRuleModal: boolean,
  // Employee SMS Rules
  employeeSmsSettings: ?EmployeeSmsSettings,
}>;

type PropsFromDispatch = $Exact<{
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
  // SMS Absence Notifications
  getManagerSmsNotificationSettings: typeof serviceActionCreators.GetManagerSmsNotificationSettings,
  updateManagerSmsNotificationSettings: typeof serviceActionCreators.UpdateManagerSmsNotificationSettings,
  getManagerSmsNotificationsSmsRules: typeof serviceActionCreators.GetManagerSmsNotificationsSmsRules,
  getManagerSmsNotificationsPagerRules: typeof serviceActionCreators.GetManagerSmsNotificationsPagerRules,
  getManagerSmsNotificationsSmsAssignments: typeof serviceActionCreators.GetManagerSmsNotificationsSmsAssignments,
  setManagerSmsNotificationCurrent: typeof serviceActionCreators.SetManagerSmsNotificationCurrent,
  setManagerSmsNotificationAssignments: typeof serviceActionCreators.SetManagerSmsNotificationAssignments,
  addManagerSmsNotificationSmsRule: typeof serviceActionCreators.AddManagerSmsNotificationSmsRule,
  addManagerSmsNotificationPagerRule: typeof serviceActionCreators.AddManagerSmsNotificationPagerRule,
  updateManagerSmsNotificationSmsRule: typeof serviceActionCreators.UpdateManagerSmsNotificationSmsRule,
  updateManagerSmsNotificationPagerRule: typeof serviceActionCreators.UpdateManagerSmsNotificationPagerRule,
  deleteManagerSmsNotificationSmsRule: typeof serviceActionCreators.DeleteManagerSmsNotificationSmsRule,
  deleteManagerSmsNotificationPagerRule: typeof serviceActionCreators.DeleteManagerSmsNotificationPagerRule,
  setShowAddManagerSmsNotificationModal: typeof serviceActionCreators.SetShowAddManagerSmsNotificationModal,
  setShowEditManagerSmsNotificationModal: typeof serviceActionCreators.SetShowEditManagerSmsNotificationModal,
  setShowDeleteManagerSmsNotificationModal: typeof serviceActionCreators.SetShowDeleteManagerSmsNotificationModal,

  // Employee SMS Rules
  getEmployeeSmsSettings: typeof serviceActionCreators.GetEmployeeSmsSettings,
  updateEmployeeSmsSettings: typeof serviceActionCreators.UpdateEmployeeSmsSettings,
}>;

type Props = $Exact<{
  clientId: number | null,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type RuleType = "SMS" | "Pager";

const RuleTypeEnum = {
  SMS: "SMS",
  Pager: "Pager",
};

const SmsSettings = (props: Props) => {
  const [ruleType, setRuleType] = useState(RuleTypeEnum.SMS);

  useEffect(() => {
    // // SMS Absence Notifications
    props.getManagerSmsNotificationSettings({ clientId: props.clientId });
    props.getManagerSmsNotificationsSmsRules({ clientId: props.clientId });
    props.getManagerSmsNotificationsPagerRules({ clientId: props.clientId });
    // // Employee SMS Rules
    props.getEmployeeSmsSettings({ clientId: props.clientId });
  }, [props.clientId]);

  const isSms = ruleType === RuleTypeEnum.SMS;
  const addManagerSmsNotificationPagerRule = isSms
    ? props.addManagerSmsNotificationSmsRule
    : props.addManagerSmsNotificationPagerRule;

  const updateManagerSmsNotificationPagerRule = isSms
    ? props.updateManagerSmsNotificationSmsRule
    : props.updateManagerSmsNotificationPagerRule;

  const deleteManagerSmsNotificationPagerRule = isSms
    ? props.deleteManagerSmsNotificationSmsRule
    : props.deleteManagerSmsNotificationPagerRule;

  function getSuccessUpdateSettingMessage(settings: ManagerSmsSettings) {
    return props.t("MANAGER_SUCCESS_NOTIFICATION_MESSAGE", {
      type:
        (props.managerSmsSettings || {}).pagerNotificationsEnabled !==
        settings.pagerNotificationsEnabled
          ? props.t("MANAGER_TYPE_PAGER")
          : props.t("MANAGER_TYPE_SMS"),
    });
  }

  function updateManagerSmsNotificationSettings(settings: ManagerSmsSettings) {
    props.updateManagerSmsNotificationSettings({
      clientId: props.clientId,
      settings,
      successMessage: getSuccessUpdateSettingMessage(settings),
    });
  }

  function closeSMSModal(closeAction: Function) {
    return function() {
      closeAction(false);
    };
  }

  function showAddSmsModal(ruleType: RuleType) {
    return function() {
      setRuleType(ruleType);
      props.setManagerSmsNotificationCurrent({});
      props.setManagerSmsNotificationAssignments([]);
      props.setShowAddManagerSmsNotificationModal(true);
    };
  }

  function handleAddRule(rule: ManagerSmsRestriction, assignments: Array<HierarchyAssignment>) {
    addManagerSmsNotificationPagerRule({
      clientId: props.clientId,
      rule,
      assignments,
    });
  }

  function showEditSmsModal(ruleType: RuleType) {
    return function(item: ManagerSmsRestriction) {
      setRuleType(ruleType);
      props.setManagerSmsNotificationCurrent(item);
      props.getManagerSmsNotificationsSmsAssignments({
        clientId: props.clientId,
        ruleId: item.id,
      });
      props.setShowEditManagerSmsNotificationModal(true);
    };
  }

  function handleEditRule(rule: ManagerSmsRestriction, assignments: Array<HierarchyAssignment>) {
    updateManagerSmsNotificationPagerRule({
      clientId: props.clientId,
      rule,
      assignments,
    });
  }

  function showDeleteSmsModal(ruleType: RuleType) {
    return function(item: ManagerSmsRestriction) {
      setRuleType(ruleType);
      props.setManagerSmsNotificationCurrent(item);
      props.setShowDeleteManagerSmsNotificationModal(true);
    };
  }

  function handleDeleteRule(ruleId: number) {
    deleteManagerSmsNotificationPagerRule({ clientId: props.clientId, ruleId });
  }

  function updateEmployeeSmsSettings(settings: EmployeeSmsSettings) {
    props.updateEmployeeSmsSettings({
      clientId: props.clientId,
      settings,
    });
  }

  return (
    <View gap="6">
      {props.isLoading && <Loader />}

      {/* SMS Absence Notifications */}
      <ManagerSmsNotificationsSmsList
        list={props.getManagerSmsNotificationsList}
        settings={props.managerSmsSettings}
        employeeSettings={props.employeeSmsSettings}
        onUpdateSettings={updateManagerSmsNotificationSettings}
        onUpdateSmsSettings={updateEmployeeSmsSettings}
        onAdd={showAddSmsModal(RuleTypeEnum.SMS)}
        onEdit={showEditSmsModal(RuleTypeEnum.SMS)}
        onDelete={showDeleteSmsModal(RuleTypeEnum.SMS)}
      />

      {/* Modals */}
      {props.getShowAddManagerSmsNotificationSmsRuleModal && (
        <ManagerSmsNotificationEditModal
          editing={false}
          type={ruleType}
          rule={props.getManagerSmsCurrent}
          assignments={props.getManagerSmsAssignments}
          onCancel={closeSMSModal(props.setShowAddManagerSmsNotificationModal)}
          onSubmit={handleAddRule}
        />
      )}
      {props.getShowEditManagerSmsNotificationModal && (
        <ManagerSmsNotificationEditModal
          editing={true}
          type={ruleType}
          rule={props.getManagerSmsCurrent}
          assignments={props.getManagerSmsAssignments}
          onCancel={closeSMSModal(props.setShowEditManagerSmsNotificationModal)}
          onSubmit={handleEditRule}
        />
      )}
      {props.getShowDeleteManagerSmsNotificationSmsRuleModal && (
        <ManagerSmsNotificationDeleteModal
          type={ruleType}
          id={(props.getManagerSmsCurrent || {}).id}
          onCancel={closeSMSModal(props.setShowDeleteManagerSmsNotificationModal)}
          onSubmit={handleDeleteRule}
        />
      )}
      {/* Modals END */}
    </View>
  );
};

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    isLoading: serviceSelectors.isLoading(state),
    // SMS Absence Notifications
    currentUserId: getUserId(state),
    managerSmsSettings: serviceSelectors.getManagerSmsSettings(state),
    getManagerSmsNotificationsList: serviceSelectors.getManagerSmsNotificationsList(state),
    getManagerSmsCurrent: serviceSelectors.getManagerSmsCurrent(state),
    getManagerSmsAssignments: serviceSelectors.getManagerSmsAssignments(state),
    getShowAddManagerSmsNotificationSmsRuleModal: serviceSelectors.getShowAddManagerSmsNotificationSmsRuleModal(
      state,
    ),
    getShowEditManagerSmsNotificationModal: serviceSelectors.getShowEditManagerSmsNotificationModal(
      state,
    ),
    getShowDeleteManagerSmsNotificationSmsRuleModal: serviceSelectors.getShowDeleteManagerSmsNotificationSmsRuleModal(
      state,
    ),
    // Employee SMS Rules
    employeeSmsSettings: serviceSelectors.getEmployeeSmsSettings(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
  // SMS Absence Notifications
  getManagerSmsNotificationSettings: serviceActionCreators.GetManagerSmsNotificationSettings,
  updateManagerSmsNotificationSettings: serviceActionCreators.UpdateManagerSmsNotificationSettings,
  getManagerSmsNotificationsSmsRules: serviceActionCreators.GetManagerSmsNotificationsSmsRules,
  getManagerSmsNotificationsPagerRules: serviceActionCreators.GetManagerSmsNotificationsPagerRules,
  getManagerSmsNotificationsSmsAssignments:
    serviceActionCreators.GetManagerSmsNotificationsSmsAssignments,
  setManagerSmsNotificationCurrent: serviceActionCreators.SetManagerSmsNotificationCurrent,
  setManagerSmsNotificationAssignments: serviceActionCreators.SetManagerSmsNotificationAssignments,
  addManagerSmsNotificationSmsRule: serviceActionCreators.AddManagerSmsNotificationSmsRule,
  addManagerSmsNotificationPagerRule: serviceActionCreators.AddManagerSmsNotificationPagerRule,
  updateManagerSmsNotificationSmsRule: serviceActionCreators.UpdateManagerSmsNotificationSmsRule,
  updateManagerSmsNotificationPagerRule:
    serviceActionCreators.UpdateManagerSmsNotificationPagerRule,
  deleteManagerSmsNotificationSmsRule: serviceActionCreators.DeleteManagerSmsNotificationSmsRule,
  deleteManagerSmsNotificationPagerRule:
    serviceActionCreators.DeleteManagerSmsNotificationPagerRule,
  setShowAddManagerSmsNotificationModal:
    serviceActionCreators.SetShowAddManagerSmsNotificationModal,
  setShowEditManagerSmsNotificationModal:
    serviceActionCreators.SetShowEditManagerSmsNotificationModal,
  setShowDeleteManagerSmsNotificationModal:
    serviceActionCreators.SetShowDeleteManagerSmsNotificationModal,

  // Employee SMS Rules
  getEmployeeSmsSettings: serviceActionCreators.GetEmployeeSmsSettings,
  updateEmployeeSmsSettings: serviceActionCreators.UpdateEmployeeSmsSettings,
};

export default compose(
  withErrorHandler({}),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(SmsSettings);
